// Filing a fax into the Athena chart, shared by the fax viewer's "Send to Athena"
// (modals/sendtoathena.jsx) and the "Send to Athena" box inside "Send to EMR"
// (modals/sendtoemr.jsx, 10-07), so both send exactly the same request.
//
// v1 POST /v1/{pid}/fax-documents/add-imaging-result (filed closed, autoclose) or
// /add-clinical-document (+ documentsubclass). The gateway files a file into a chart
// once per class (ledger key fax:<class>:<patient>:<sha256>) and asks before a second
// copy: 409 already_filed / athena_unknown, answered by the api client's confirm
// (../api/athenaConfirm.js). The faxid as Send to Athena always sent. departmentid: the
// Athena department of the filing location (config/practice.js athenaDepartmentId):
// "1" for Odessa / Frisco exactly as before, "3" for CODE (9928 dept 3).
import api from "../api/api";
import careiqapi from "../api/careiqapi";
import { athenaDepartmentId, practiceFaxProfile } from "../config/practice";

// Class the modals open on when the fax has no AI read (per practice; staff can
// always switch Imaging / Clinical document).
export function defaultAthenaCategory() {
  const c = practiceFaxProfile()?.athenaDefaultCategory;
  return c === "CLINICAL" ? "CLINICAL" : "IMAGING";
}

export const CLINICAL_SUB_TYPES = [
  "CLINICALDOCUMENT",
  "ADMISSIONDISCHARGE",
  "CONSULTNOTE",
  "MENTALHEALTH",
  "OPERATIVENOTE",
  "URGENTCARE",
];

// "CONSULTNOTE" -> " C O N S U L T N O T E" is how Send to Athena has always shown them.
export const clinicalSubTypeLabel = (t) => t.replace(/([A-Z])/g, " $1");

// The Athena document class each category files as (the gateway's `filed_as`).
export const ATHENA_DOC_CLASS = Object.freeze({ IMAGING: "imagingresult", CLINICAL: "clinicaldocument" });

/*
 * A unique AI match as the patient pick both modals use:
 * { patientid, patientinfo: "<name> - <id>", dob }.
 */
export const pickOf = (p) =>
  p ? { patientid: p.patientid, patientinfo: `${p.name} - ${p.patientid}`, dob: p.dob || "" } : null;

// The same fax (fax_master id, else the list's message id). The pre-fill is read
// from aiRow only when it is the fax being uploaded; otherwise from selectedFax
// itself, so a patient can never come from another fax's read.
export const sameFax = (a, b) => {
  if (!a || !b) return false;
  const has = (v) => v !== undefined && v !== null && v !== "";
  if (has(a.faxid) && has(b.faxid)) return String(a.faxid) === String(b.faxid);
  return has(a.id) && has(b.id) && String(a.id) === String(b.id);
};

// The id recorded with the Athena id (fax_master id, else the list's message id), or null.
export const faxRefOf = (fax) => {
  const ref = fax?.faxid ?? fax?.id;
  return ref === undefined || ref === null || ref === "" ? null : String(ref);
};

/**
 * File one file into the patient's Athena chart. Resolves to the gateway's body
 * ({athena_id, ...}; an older gateway may answer {duplicate_prevented: true}); rejects
 * with the axios error (a 409 already_filed the person declined keeps its body:
 * {code, filed_as, athena_id}).
 */
export function fileFaxInAthena({ practiceId, patientId, file, category, subclass, internalNote, faxRef, departmentId }) {
  const formData = new FormData();
  formData.append("patientid", patientId);
  // departmentId = the filing staff member's location; no location -> 1.
  formData.append("departmentid", Number(athenaDepartmentId(departmentId)));
  formData.append("internalnote", internalNote || "");
  formData.append("file", file);
  // Recorded with the Athena id: the gateway files a fax into a chart once and
  // answers a repeat with the copy already there (2026-10-06).
  if (faxRef !== undefined && faxRef !== null && faxRef !== "") formData.append("faxid", String(faxRef));
  // The api client defaults to JSON, which mangles FormData (the server then 400s on
  // "required fields"): multipart, as every FormData call must.
  const multipart = { headers: { "Content-Type": "multipart/form-data" } };

  const refreshed = (res) => {
    const docClass = ATHENA_DOC_CLASS[category === "CLINICAL" ? "CLINICAL" : "IMAGING"];
    addFiledDocumentToList(practiceId, patientId, docClass, res, {
      documentsubclass: category === "CLINICAL" ? subclass : undefined,
      title: category === "CLINICAL" ? undefined : (internalNote || "").trim() || undefined,
    });
    return res;
  };
  if (category === "CLINICAL") {
    formData.append("documentsubclass", subclass);
    return api.post(`/v1/${practiceId}/fax-documents/add-clinical-document`, formData, multipart).then(refreshed);
  }
  formData.append("autoclose", true);
  formData.append("documenttypeid", "");
  formData.append("observationtime", "");
  formData.append("observationdate", "");
  formData.append("attachmenttype", "");
  return api.post(`/v1/${practiceId}/fax-documents/add-imaging-result`, formData, multipart).then(refreshed);
}

// cache-add's 200 answers (v2 contract, 10-15) that leave the list right: the document
// was added, was already listed, or no list is cached yet (the next chart open builds
// it from Athena and finds the document). "failed" (or anything else) did not.
const LIST_OK = ["added", "already_listed", "no_cache"];
// A refused request the old rebuild would be refused for too (auth): no rebuild.
const AUTH_REFUSED = [401, 403];

// Today as YYYY-MM-DD (local), the filing date sent with cache-add.
const todayIso = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/*
 * After a filing, put the new document into the patient's cached Athena document list
 * for that class (v2 fhir-mirror .../documents/{class}/cache-add), so the chart's
 * Documents timeline shows the fax now instead of after the list's 24 h cache (owner
 * 10-14: Athena is the only place a fax goes). No Athena call: the old way rebuilt the
 * whole list from Athena (?_skip_cache=1, a billed list read) on every filing (Athena
 * call plan item 8, 10-15).
 *  - The class is the one the gateway says it is filed as (a repeat may be on file
 *    under the other class), else the class asked for.
 *  - Whenever cache-add did not put the document in the list, the old rebuild, once,
 *    so the fax still shows at once (review 10-15): a v2 without cache-add (404/405), a
 *    5xx, a network error or timeout, a 400/422, or a 200 whose list is "failed". Not
 *    on 401/403 (the rebuild would be refused too). Also a filing answer with no Athena
 *    id (nothing to add; the document may be new).
 *  - Fire-and-forget: it never affects the filing.
 */
export function addFiledDocumentToList(practiceId, patientId, docClass, res, { documentsubclass, title } = {}) {
  if (!practiceId || !patientId) return;
  const filedAs = Object.values(ATHENA_DOC_CLASS).includes(res?.filed_as) ? res.filed_as : docClass;
  if (!filedAs) return;
  const athenaId = res?.athena_id;
  if (athenaId === undefined || athenaId === null || athenaId === "") {
    refreshPatientDocuments(practiceId, patientId, filedAs);
    return;
  }
  const body = { athena_id: String(athenaId), filed_as: filedAs, date: todayIso() };
  // A clinical document is named by its subclass; an imaging result by the staff note, if any.
  if (filedAs === ATHENA_DOC_CLASS.CLINICAL && documentsubclass) body.documentsubclass = documentsubclass;
  else if (filedAs === ATHENA_DOC_CLASS.IMAGING && title) body.title = title;
  const rebuild = () => refreshPatientDocuments(practiceId, patientId, filedAs);
  let p;
  try {
    p = careiqapi.post(
      `/v1/fhir-mirror/${practiceId}/patients/${patientId}/documents/${filedAs}/cache-add`,
      body,
    );
  } catch (e) {
    rebuild(); // never let the list update break a filing
    return;
  }
  Promise.resolve(p).then(
    (answer) => {
      if (!LIST_OK.includes(answer?.list)) rebuild();
    },
    (e) => {
      if (!AUTH_REFUSED.includes(e?.response?.status)) rebuild();
    },
  ).catch(() => {});
}

/*
 * The old way: rebuild the patient's cached Athena document list for that class (v2
 * fhir-mirror ?_skip_cache=1, written back to its cache; one billed Athena list call).
 * Only when cache-add did not add the document (see addFiledDocumentToList), or a
 * filing answer without an Athena id.
 */
export function refreshPatientDocuments(practiceId, patientId, docClass) {
  if (!practiceId || !patientId || !docClass) return;
  try {
    const p = careiqapi.get(
      `/v1/fhir-mirror/${practiceId}/patients/${patientId}/documents/${docClass}`,
      { params: { _skip_cache: 1 } },
    );
    if (p && typeof p.catch === "function") p.catch(() => {});
  } catch (e) {
    /* never let the refresh break a filing */
  }
}
